import React from 'react'
import { Button, Table, Tabs } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import {
  BiDesempenhoCursosStore,
  CursoMetric,
  Granularity,
  currentPeriodStart,
  periodBucket,
  periodEnd,
  shiftPeriod,
} from './BiDesempenhoCursos'
import { CURSO_COLORS } from './DesempenhoCursosChart'

const FALLBACK_COLOR = '#94a3b8'
const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

interface SummaryRow {
  cursoId: number
  nomeReduzido: string
  total: number
}

interface PeriodoRow {
  cursoId: number
  nomeReduzido: string
  previous: number | null
  current: number | null
  next: number | null
}

export function DesempenhoCursosTable() {
  return (
    <Tabs
      style={{ marginTop: 16 }}
      items={[
        { key: 'total', label: 'Período completo', children: <TotalTable /> },
        { key: 'periodo', label: <PeriodoTabLabel />, children: <PeriodoTable /> },
      ]}
    />
  )
}

function TotalTable() {
  const { courses, points, selectedCursoIds, metric } = BiDesempenhoCursosStore.useState()

  const rows: SummaryRow[] = courses
    .filter((c) => selectedCursoIds.includes(c.id))
    .map((course) => {
      const total = points
        .filter((p) => p.cursoId === course.id)
        .reduce((sum, p) => sum + p[metric], 0)
      return { cursoId: course.id, nomeReduzido: course.nomeReduzido, total }
    })
    .sort((a, b) => b.total - a.total)

  const maxTotal = rows[0]?.total ?? 0

  const columns: ColumnsType<SummaryRow> = [
    {
      title: 'Curso',
      dataIndex: 'nomeReduzido',
      render: (name: string, row: SummaryRow) => <CourseName cursoId={row.cursoId} name={name} />,
    },
    {
      title: metricTitle(metric),
      dataIndex: 'total',
      render: (value: number) => (
        <BarCell value={value} max={maxTotal} formatted={formatValue(value, metric)} />
      ),
    },
  ]

  return <Table columns={columns} dataSource={rows} rowKey="cursoId" pagination={false} size="small" />
}

function PeriodoTabLabel() {
  const { granularity } = BiDesempenhoCursosStore.useState()
  const labels: Record<Granularity, string> = { daily: 'Por dia', weekly: 'Por semana', monthly: 'Por mês' }
  return <>{labels[granularity]}</>
}

function PeriodoTable() {
  const { courses, periodPoints, periodLoading, selectedCursoIds, metric, granularity, periodStart } =
    BiDesempenhoCursosStore.useState()

  const previousStart = shiftPeriod(periodStart, granularity, -1)
  const nextStart = shiftPeriod(periodStart, granularity, 1)
  const valueByCursoAndBucket = new Map(periodPoints.map((p) => [`${p.cursoId}:${p.bucket}`, p[metric]]))

  function valueAt(cursoId: number, start: string): number | null {
    return valueByCursoAndBucket.get(`${cursoId}:${periodBucket(start, granularity)}`) ?? null
  }

  const rows: PeriodoRow[] = courses
    .filter((c) => selectedCursoIds.includes(c.id))
    .map((course) => ({
      cursoId: course.id,
      nomeReduzido: course.nomeReduzido,
      previous: valueAt(course.id, previousStart),
      current: valueAt(course.id, periodStart),
      next: valueAt(course.id, nextStart),
    }))
    .sort((a, b) => (b.current ?? 0) - (a.current ?? 0))

  const totals = {
    previous: sumNullable(rows.map((r) => r.previous)),
    current: sumNullable(rows.map((r) => r.current)),
    next: sumNullable(rows.map((r) => r.next)),
  }
  const maxCurrent = Math.max(0, ...rows.map((r) => r.current ?? 0))

  const columns: ColumnsType<PeriodoRow> = [
    {
      title: 'Curso',
      dataIndex: 'nomeReduzido',
      render: (name: string, row: PeriodoRow) => <CourseName cursoId={row.cursoId} name={name} />,
    },
    {
      title: `Anterior (${formatPeriodLabel(previousStart, granularity)})`,
      dataIndex: 'previous',
      render: (value: number | null) => formatNullable(value, metric),
    },
    {
      title: <strong>{formatPeriodLabel(periodStart, granularity)}</strong>,
      dataIndex: 'current',
      render: (value: number | null, row: PeriodoRow) => (
        <BarCell value={value ?? 0} max={maxCurrent} formatted={formatNullable(value, metric)}>
          <Delta value={value} reference={row.previous} metric={metric} />
        </BarCell>
      ),
    },
    {
      title: `Próximo (${formatPeriodLabel(nextStart, granularity)})`,
      dataIndex: 'next',
      render: (value: number | null, row: PeriodoRow) => (
        <>
          {formatNullable(value, metric)}
          <Delta value={value} reference={row.current} metric={metric} />
        </>
      ),
    },
  ]

  return (
    <>
      <PeriodNavigator />
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="cursoId"
        pagination={false}
        size="small"
        loading={periodLoading}
        summary={() => (
          <Table.Summary.Row style={{ fontWeight: 600 }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell index={1}>{formatNullable(totals.previous, metric)}</Table.Summary.Cell>
            <Table.Summary.Cell index={2}>
              {formatNullable(totals.current, metric)}
              <Delta value={totals.current} reference={totals.previous} metric={metric} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              {formatNullable(totals.next, metric)}
              <Delta value={totals.next} reference={totals.current} metric={metric} />
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </>
  )
}

function PeriodNavigator() {
  const { tipo, granularity, periodStart, periodLoading } = BiDesempenhoCursosStore.useState()
  const dispatch = BiDesempenhoCursosStore.useDispatch()
  const isCurrentPeriod = periodStart >= currentPeriodStart(granularity)

  function goTo(start: string) {
    dispatch(BiDesempenhoCursosStore.thunks.fetchPeriodData(tipo, granularity, start))
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
      <Button disabled={periodLoading} onClick={() => goTo(shiftPeriod(periodStart, granularity, -1))}>
        ←
      </Button>
      <strong style={{ minWidth: 140, textAlign: 'center' }}>{formatPeriodLabel(periodStart, granularity)}</strong>
      <Button
        disabled={periodLoading || isCurrentPeriod}
        onClick={() => goTo(shiftPeriod(periodStart, granularity, 1))}
      >
        →
      </Button>
      <Button disabled={periodLoading || isCurrentPeriod} onClick={() => goTo(currentPeriodStart(granularity))}>
        Atual
      </Button>
    </div>
  )
}

function CourseName({ cursoId, name }: { cursoId: number; name: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: CURSO_COLORS[cursoId] ?? FALLBACK_COLOR,
          flexShrink: 0,
          display: 'inline-block',
        }}
      />
      {name}
    </span>
  )
}

function Delta({ value, reference, metric }: { value: number | null; reference: number | null; metric: CursoMetric }) {
  if (value === null || reference === null) return null
  const delta = value - reference
  if (delta === 0) return null
  const arrow = delta > 0 ? '▲' : '▼'
  const color = delta > 0 ? '#22c55e' : '#ef4444'
  const percent = reference !== 0 ? ` (${Math.abs(Math.round((delta / reference) * 100))}%)` : ''
  return (
    <span style={{ color, marginLeft: 6, fontSize: 12 }}>
      {arrow} {formatValue(Math.abs(delta), metric)}
      {percent}
    </span>
  )
}

function BarCell({
  value,
  max,
  formatted,
  children,
}: {
  value: number
  max: number
  formatted: string
  children?: React.ReactNode
}) {
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: `${pct}%`,
          background: 'rgba(79,142,247,0.18)',
          borderRadius: 2,
        }}
      />
      <span style={{ position: 'relative' }}>
        {formatted}
        {children}
      </span>
    </div>
  )
}

function formatPeriodLabel(periodStart: string, granularity: Granularity): string {
  const start = dayjs(periodStart)
  if (granularity === 'daily') return start.format('DD/MM/YYYY')
  if (granularity === 'weekly') return `${start.format('DD/MM')} a ${dayjs(periodEnd(periodStart, granularity)).format('DD/MM')}`
  return `${MONTHS[start.month()]}/${start.year()}`
}

function sumNullable(values: (number | null)[]): number | null {
  const present = values.filter((v): v is number => v !== null)
  return present.length === 0 ? null : present.reduce((sum, v) => sum + v, 0)
}

function metricTitle(metric: CursoMetric): string {
  return metric === 'count' ? 'Matrículas' : 'Receita NewDriver'
}

function formatNullable(value: number | null, metric: CursoMetric): string {
  return value === null ? '—' : formatValue(value, metric)
}

function formatValue(value: number, metric: CursoMetric): string {
  if (metric === 'valorNewDriverSum') {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }
  return String(value)
}
